import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state, sequences } from 'app/pwa/app.cerebral';
import { useText, useLink } from 'common/hooks';
import { ICON } from 'resources/constants';
import PageContainer from '../common/PageContainer';
import {
  BackIconWrapper,
  BackIcon,
  Text,
  RadioWrapper,
  Radio,
  FeedbackInput,
  WarningWrapper,
  WarningText,
  WarningIcon,
  Input,
  DeleteButton,
} from './elements';

import { PageWrapper, Title } from '../elements';

const deps = {
  deleteAccountForm: state.account.verifyDeletionForm,
  isValidDeletionForm: state.account.isValidDeletionForm,
  isDeletingAccount: state.account.isDeletingAccount,
  changeFeedbackValue: sequences`account.changeFeedbackValue`,
  feedbackFieldChanged: sequences`account.feedbackFieldChanged`,
  deleteFieldChanged: sequences`account.deleteFieldChanged`,
  verifiedDeleteAccount: sequences`account.verifiedDeleteAccountClicked`,
};

export function ProfileDelete({
  deleteAccountForm,
  isValidDeletionForm,
  isDeletingAccount,
  changeFeedbackValue,
  feedbackFieldChanged,
  deleteFieldChanged,
  verifiedDeleteAccount,
}: typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const link = useLink();

  const radioContent = {
    noLongerNeed: t('account.settings.profile.delete.no_longer_need'),
    notExpected: t('account.settings.profile.delete.not_what_expected'),
    notWorking: t('account.settings.profile.delete.not_working'),
    challengeOver: t('account.settings.profile.delete.challenge_ended'),
    other: t('account.settings.profile.delete.other'),
  };

  return (
    <PageContainer tab={'profile'}>
      <PageWrapper>
        <BackIconWrapper>
          <a href={link('profile')}>
            <BackIcon icon={ICON.keyboardArrowLeft} />
          </a>
        </BackIconWrapper>
        <Title as={'h2'}>
          {t('account.settings.profile.delete.delete_account')}
        </Title>
        <Text>{t('account.settings.profile.delete.sorry_you_leave')}</Text>
        <RadioWrapper>
          {Object.entries(radioContent).map(([key, label]) => (
            <Radio
              key={`radio-delete-reason-${key}`}
              onClick={() => changeFeedbackValue({ value: key })}
              label={label}
              checked={deleteAccountForm.feedbackChecked.value === key}
            />
          ))}
          {deleteAccountForm.feedbackChecked.value === 'other' && (
            <FeedbackInput
              icon={'icon-mode_edit'}
              onChange={event => {
                feedbackFieldChanged({
                  value: event.target.value,
                });
              }}
              placeholder={t('account.settings.profile.delete.please_specify')}
              value={deleteAccountForm.feedbackText.value}
              label={t('account.settings.profile.delete.please_specify')}
            />
          )}
        </RadioWrapper>
        <WarningWrapper>
          <WarningIcon icon={ICON.errorOutline} />
          <WarningText>
            {t('account.settings.profile.delete.cannot_reverse')}
          </WarningText>
        </WarningWrapper>
        <Input
          onChange={event => {
            deleteFieldChanged({
              value: event.target.value,
            });
          }}
          value={deleteAccountForm.verifyText.value}
          placeholder={t('account.settings.profile.delete.type_delete')}
        />

        <DeleteButton
          disabled={!isValidDeletionForm || isDeletingAccount}
          onClick={() =>
            verifiedDeleteAccount({
              feedback: {
                radio: deleteAccountForm.feedbackChecked.value || 'noFeedback',
                text: deleteAccountForm.feedbackText.value || '',
              },
            })
          }
        >
          {t('account.settings.profile.delete.delete_button')}
        </DeleteButton>
      </PageWrapper>
    </PageContainer>
  );
}

export default connect(deps, ProfileDelete);
